import { truncate } from './util';

const MASK = '[REDACTED]';

/** Light-mode defaults (PLAN.md §2.1/4): credentials and card data, nothing else. */
export const DEFAULT_FIELDS = ['password', 'password_confirmation', 'token', '_token', 'card_number', 'cvv', 'secret', 'api_key'];
export const DEFAULT_HEADERS = ['authorization', 'cookie', 'set-cookie', 'x-csrf-token', 'x-xsrf-token'];

/** Value patterns masked inside free text: card PAN (13-19 digits), IBAN, bearer tokens. TC kimlik / phone are kept on purpose. */
export const DEFAULT_PATTERNS: RegExp[] = [
  /\b\d(?:[ -]?\d){12,18}\b/g,
  /\b[A-Z]{2}\d{2}(?:[ -]?[A-Z0-9]){11,30}\b/g,
  /\b(Bearer|Basic)\s+[A-Za-z0-9\-._~+/=]{8,}/g,
];

export interface RedactorOptions {
  fields?: string[];
  headers?: string[];
  patterns?: RegExp[];
  maxBodySize?: number;
}

export class Redactor {
  private fields: string[];
  private flatFields: string[];
  private headers: string[];
  private patterns: RegExp[];
  maxBodySize: number;

  constructor(opts: RedactorOptions = {}) {
    this.fields = [...DEFAULT_FIELDS, ...(opts.fields ?? [])].map((f) => f.toLowerCase());
    this.flatFields = this.fields.map((f) => f.replace(/[^a-z0-9]/g, '')).filter((f) => f.length >= 5);
    this.headers = [...DEFAULT_HEADERS, ...(opts.headers ?? [])].map((h) => h.toLowerCase());
    this.patterns = [...DEFAULT_PATTERNS, ...(opts.patterns ?? [])];
    this.maxBodySize = opts.maxBodySize ?? 16 * 1024;
  }

  /** Extend at runtime with lists from GET /v1/config. */
  extend(opts: RedactorOptions): void {
    if (opts.fields) {
      this.fields = Array.from(new Set([...this.fields, ...opts.fields.map((f) => f.toLowerCase())]));
      this.flatFields = this.fields.map((f) => f.replace(/[^a-z0-9]/g, '')).filter((f) => f.length >= 5);
    }
    if (opts.headers) this.headers = Array.from(new Set([...this.headers, ...opts.headers.map((h) => h.toLowerCase())]));
    if (opts.patterns) this.patterns.push(...opts.patterns);
    if (opts.maxBodySize) this.maxBodySize = opts.maxBodySize;
  }

  isSensitiveField(key: string): boolean {
    const k = key.toLowerCase();
    if (this.fields.includes(k)) return true;
    const flat = k.replace(/[^a-z0-9]/g, '');
    return this.flatFields.some((f) => flat.includes(f));
  }

  headersOf(headers: Record<string, string> | null | undefined): Record<string, string> | undefined {
    if (!headers) return undefined;
    const out: Record<string, string> = {};
    for (const name of Object.keys(headers)) {
      out[name] = this.headers.includes(name.toLowerCase()) ? MASK : headers[name]!;
    }
    return out;
  }

  body(body: unknown): string | null {
    if (body === null || body === undefined || body === '') return null;
    if (typeof body === 'string') return this.string(body);
    if (typeof body === 'object') return truncate(this.stringifyMasked(body), this.maxBodySize);
    return this.string(String(body));
  }

  string(text: string): string {
    const trimmed = text.trimStart();
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        return truncate(this.stringifyMasked(JSON.parse(text)), this.maxBodySize);
      } catch {
        /* not JSON */
      }
    }
    if (/^[\w.%+\-[\]]+=[^&]*(&[\w.%+\-[\]]+=[^&]*)*$/.test(text)) {
      const params = new URLSearchParams(text);
      const out = new URLSearchParams();
      params.forEach((v, k) => out.append(k, this.isSensitiveField(k) ? MASK : v));
      return truncate(out.toString(), this.maxBodySize);
    }
    return truncate(this.text(text), this.maxBodySize);
  }

  /** Regex-only masking for free text (messages, stacks). */
  text(text: string): string {
    let out = text;
    for (const field of this.fields) {
      const f = field.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      out = out.replace(new RegExp(`(["']${f}["']\\s*[:=>]+\\s*["'])([^"']*)(["'])`, 'gi'), `$1${MASK}$3`);
      out = out.replace(new RegExp(`\\b(${f}=)([^&\\s]*)`, 'gi'), `$1${MASK}`);
    }
    for (const p of this.patterns) {
      out = out.replace(p, (m, g1?: string) => (g1 && /^(Bearer|Basic)$/.test(g1) ? `${g1} ${MASK}` : MASK));
    }
    return out;
  }

  private stringifyMasked(value: unknown): string {
    return JSON.stringify(this.walk(value));
  }

  private walk(value: unknown): unknown {
    if (Array.isArray(value)) return value.map((v) => this.walk(v));
    if (value && typeof value === 'object') {
      const out: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
        out[k] = this.isSensitiveField(k) ? MASK : this.walk(v);
      }
      return out;
    }
    if (typeof value === 'string') return this.text(value);
    return value;
  }
}
